import { useState, useRef, useCallback } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { ArrowsClockwise } from '@phosphor-icons/react';
import { useTheme } from '@/contexts/ThemeContext';

/**
 * Pull to Refresh component
 * Wrap content that should be refreshable
 */
export default function PullToRefresh({ children, onRefresh, disabled = false }) {
  const { isDark } = useTheme();
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const containerRef = useRef(null);
  const startY = useRef(0);
  const controls = useAnimation();
  
  const THRESHOLD = 80; // Distance to trigger refresh
  const MAX_PULL = 120; // Maximum pull distance
  
  const handleTouchStart = useCallback((e) => {
    if (disabled || isRefreshing) return;
    
    // Only activate if scrolled to top
    const container = containerRef.current;
    if (!container || container.scrollTop > 5) return;
    
    startY.current = e.touches[0].clientY;
    setIsPulling(true);
  }, [disabled, isRefreshing]);
  
  const handleTouchMove = useCallback((e) => {
    if (!isPulling || disabled || isRefreshing) return;
    
    const container = containerRef.current;
    if (!container || container.scrollTop > 5) {
      setIsPulling(false);
      setPullDistance(0);
      return;
    }
    
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    
    if (diff > 0) {
      // Apply resistance to make it feel natural
      const resistance = 0.5;
      const distance = Math.min(diff * resistance, MAX_PULL);
      setPullDistance(distance);
      
      // Prevent default scroll when pulling
      if (distance > 10) {
        e.preventDefault();
      }
    }
  }, [isPulling, disabled, isRefreshing]);
  
  const handleTouchEnd = useCallback(async () => {
    if (!isPulling || disabled) return;
    
    setIsPulling(false);
    
    if (pullDistance >= THRESHOLD && onRefresh) {
      setIsRefreshing(true);
      setPullDistance(THRESHOLD);
      
      try {
        await onRefresh();
      } catch (error) {
        console.error('Refresh error:', error);
      } finally {
        // Animate back
        await controls.start({ y: 0, opacity: 0 });
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      // Snap back
      setPullDistance(0);
    }
  }, [isPulling, pullDistance, onRefresh, disabled, controls]);
  
  const progress = Math.min(pullDistance / THRESHOLD, 1);
  const rotation = progress * 360;
  
  return (
    <div 
      ref={containerRef}
      className="relative h-full overflow-auto"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ 
        touchAction: isPulling && pullDistance > 10 ? 'none' : 'auto',
        overscrollBehavior: 'contain'
      }}
    >
      {/* Pull indicator */}
      <motion.div
        initial={{ y: -60, opacity: 0 }}
        animate={{ 
          y: pullDistance > 0 ? pullDistance - 60 : -60,
          opacity: pullDistance > 10 ? 1 : 0
        }}
        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        className="absolute left-0 right-0 flex justify-center z-50 pointer-events-none"
        style={{ top: 0 }}
      >
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg ${
          isDark 
            ? 'bg-white/10 backdrop-blur-md border border-white/20' 
            : 'bg-white border border-gray-200'
        }`}>
          <motion.div
            animate={{ 
              rotate: isRefreshing ? 360 : rotation,
            }}
            transition={isRefreshing ? { 
              repeat: Infinity, 
              duration: 1, 
              ease: 'linear' 
            } : { 
              duration: 0 
            }}
          >
            <ArrowsClockwise 
              size={20} 
              weight={progress >= 1 || isRefreshing ? 'bold' : 'regular'}
              className={`${
                progress >= 1 || isRefreshing
                  ? 'text-violet-500' 
                  : isDark ? 'text-white/60' : 'text-gray-500'
              }`}
            />
          </motion.div>
          <span className={`text-sm font-medium ${
            isDark ? 'text-white/80' : 'text-gray-700'
          }`}>
            {isRefreshing 
              ? 'Actualisation...' 
              : progress >= 1 
                ? 'Relâchez !' 
                : 'Tirez pour actualiser'
            }
          </span>
        </div>
      </motion.div>
      
      {/* Content with pull transform */}
      <motion.div
        animate={{ 
          y: pullDistance > 0 ? pullDistance * 0.3 : 0 
        }}
        transition={{ type: 'spring', damping: 30, stiffness: 400 }}
        className="min-h-full"
      >
        {children}
      </motion.div>
    </div>
  );
}
